import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Database, Check, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useProfileStore } from '../lib/supabase';

export default function EmailConfirmation() {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsAuthenticated } = useProfileStore();

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      try {
        console.log('Starting email confirmation flow');

        // Get the current session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Session error:', sessionError);
          throw sessionError;
        }

        if (session?.user) {
          console.log('User authenticated:', session.user);
          // Add longer delay to ensure profile is created
          await new Promise(resolve => setTimeout(resolve, 2000));
          setIsAuthenticated(true);
          setStatus('success');
          // Further increased delay before redirect
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 3000);
        } else {
          throw new Error('No active session found');
        }
      } catch (error) {
        console.error('Email confirmation error:', error);
        setErrorMessage(
          error instanceof Error 
            ? error.message 
            : 'Email verification failed. Please try signing in again.'
        );
        setStatus('error');
      }
    };

    handleEmailConfirmation();
  }, [navigate, setIsAuthenticated]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="bg-white/5 p-8 rounded-lg shadow-xl border border-white/10 w-full max-w-md text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-white border-t-transparent mx-auto mb-4" />
          <p className="text-white/60">Verifying your email...</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="bg-white/5 p-8 rounded-lg shadow-xl border border-white/10 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold mb-4 text-white">Email Confirmed!</h2>
          <p className="text-white/60 mb-6">
            Your email has been successfully verified. You can now sign in to your account.
          </p>
          <p className="text-white/40 text-sm">
            Redirecting you to the home page...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-black">
      <div className="bg-white/5 p-8 rounded-lg shadow-xl border border-white/10 w-full max-w-md text-center">
        <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <X className="w-8 h-8 text-red-500" />
        </div>
        <h2 className="text-2xl font-bold mb-4 text-white">Verification Failed</h2>
        <p className="text-white/60 mb-6 whitespace-pre-wrap">
          {errorMessage || "We couldn't verify your email. The link might be expired or invalid."}
        </p>
        <div className="space-y-3">
          <Link
            to="/auth"
            className="block w-full py-2 px-4 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium transition-colors"
          >
            Try Signing In
          </Link>
          <Link
            to="/"
            className="block w-full py-2 px-4 text-white/60 hover:text-white transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}